import React, { useState, useEffect } from "react";
import { X, Plus, ExternalLink } from "lucide-react";
import {
  inviteMember,
  fetchSupervisors,
  fetchMemberApi,
  updateMemberAPI,
} from "../../actioncreators/actioncreators";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import { useSelector } from "react-redux";
import { useSuspension } from "../../contexts/SuspensionContext";
import { toast } from "react-toastify";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  OutlinedInput,
} from "@mui/material";
const AddMember = ({ isOpen, onClose, onSuccess, editData = null }) => {
  // Suspension check
  const { isSuspended } = useSuspension();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    employeeId: "",
    designation: "",
    mobile: "",
    supervisorIds: [], // Changed to array for multiple supervisor selection
    roleId: "",
    aadhaarAuthentication: false,
    dob: "",
  });
  const orgData = useSelector((state) => state.OrgData);
  const orgid = useSelector((state) => state.fetchOrgId);

  // Super Admin org selection support
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  const loginResponse = useSelector((state) => state.loginResponse);
  const isSuperAdmin = loginResponse?.role_name?.toUpperCase() === 'SUPER_ADMIN' || loginResponse?.roleName?.toUpperCase() === 'SUPER_ADMIN';

  // Resolve the correct org_id to use for API calls:
  // Super Admin uses the selected org; regular users fall back to loginResponse.org_id
  const effectiveOrgId = isSuperAdmin && superAdminSelectedOrgId
    ? superAdminSelectedOrgId
    : (orgid?.orgId || loginResponse?.org_id || loginResponse?.orgId);
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const country = isSuperAdmin && superAdminSelectedOrgData
    ? superAdminSelectedOrgData?.country || superAdminSelectedOrgData?.organization?.country
    : orgData?.organization?.country;
 
  const [supervisors, setSupervisors] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState("");

  // Populate form data when editData is provided
  useEffect(() => {
    if (editData) {
      // Handle supervisor_id - convert to array if single value.
      // Guard every branch: supervisor_ids may arrive as an array, a Postgres
      // array string ("{id1,id2}"), or be absent — never let a non-array reach
      // formData.supervisorIds (the MUI multi-select maps over it and a
      // non-array crashes the whole modal to a blank screen).
      let supervisorIds = [];
      if (Array.isArray(editData.supervisor_ids)) {
        supervisorIds = editData.supervisor_ids;
      } else if (typeof editData.supervisor_ids === "string" && editData.supervisor_ids.trim()) {
        supervisorIds = editData.supervisor_ids.replace(/^\{|\}$/g, "").split(",").map(s => s.trim()).filter(Boolean);
      } else if (editData.supervisor_id) {
        supervisorIds = [editData.supervisor_id];
      }

      // Safely format DOB for the <input type="date">. An invalid/unparseable
      // date makes new Date(...).toISOString() throw a RangeError, which would
      // also blank the modal during prefill.
      let dobValue = "";
      if (editData.dob) {
        const d = new Date(editData.dob);
        if (!isNaN(d.getTime())) {
          dobValue = d.toISOString().split("T")[0];
        }
      }

      setFormData({
        fullName: editData.full_name || "",
        email: editData.email || "",
        employeeId: editData.employee_id || "",
        designation: editData.designation || "",
        mobile: editData.mobile || "",
        supervisorIds: supervisorIds,
        roleId: editData.role_name || editData.role_id || "",
        aadhaarAuthentication: editData.aadhaar_authentication || false,
        dob: dobValue,
      });
      // Set the selected role name for edit mode
      if (editData.role_name) {
        setSelectedRoleName(editData.role_name);
      }
    } else {
      // Reset form when not in edit mode
      setFormData({
        fullName: "",
        email: "",
        employeeId: "",
        designation: "",
        mobile: "",
        supervisorIds: [],
        roleId: "",
        aadhaarAuthentication: false,
        dob: "",
      });
      setSelectedRoleName("");
    }
  }, [editData]);

  useEffect(() => {
    const checkMemberListAndFetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch roles and supervisors in parallel — always fetch both regardless of member count
        await Promise.all([loadRoles(), fetchSupervisor()]);
        
      } catch (err) {
        setError(err.message);
        console.error("Error in checkMemberListAndFetchData:", err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchSupervisor = async (forRole = "MEMBER") => {
      try {
        // Determine which supervisor role to fetch based on the selected role
        // MEMBER -> show LOCATION_MANAGER supervisors
        // LOCATION_MANAGER -> show ORG_ADMIN supervisors
        let supervisorRole = "MANAGER";
        if (forRole === "MANAGER") {
          supervisorRole = "ADMIN";
        }

        const payload = {
          entity_type: "user",
          columns: ["user_id", "full_name"],
          parameter: [
            {
              columnName: "status",
              condition: "=",
              value: "ACTIVE",
            },
            {
              columnName: "role_name",
              condition: "=",
              value: supervisorRole,
            },
            {
              columnName: "org_id",
              condition: "=",
              value: effectiveOrgId,
            },
          ],
        };
        const response = await fetchSupervisors(payload);
        console.log("Supervisors response for role", forRole, ":", response);

        // Check if the response contains the data as expected
        if (response.businessStatusCode === 2) {
          setSupervisors(response?.list || []);
        } else {
          throw new Error("Failed to fetch supervisors");
        }
      } catch (err) {
        throw new Error(err.message);
      }
    };

    const loadRoles = () => {
      // Roles are static strings — no separate role table exists in the DB.
      const currentUserRole = loginResponse?.role_name?.toUpperCase() || loginResponse?.roleName?.toUpperCase() || 'MEMBER';
      const roleHierarchy = { 'SUPER_ADMIN': 100, 'ADMIN': 80, 'MANAGER': 40, 'MEMBER': 20 };
      const currentUserLevel = roleHierarchy[currentUserRole] || 0;
      const allRoles = [
        { role_name: 'ADMIN' },
        { role_name: 'MANAGER' },
        { role_name: 'MEMBER' },
      ];
      const filtered = allRoles.filter(r => (roleHierarchy[r.role_name] || 0) <= currentUserLevel);
      setRoles(filtered);
    };

    checkMemberListAndFetchData();
  }, [effectiveOrgId]);

  // Refetch supervisors when the selected role changes
  useEffect(() => {
    const fetchSupervisorForRole = async () => {
      if (!effectiveOrgId || !selectedRoleName) return;

      try {
        // Determine which supervisor role to fetch based on the selected role
        // MEMBER -> show LOCATION_MANAGER supervisors
        // LOCATION_MANAGER -> show ORG_ADMIN supervisors
        let supervisorRole = "MANAGER";
        if (selectedRoleName === "MANAGER") {
          supervisorRole = "ADMIN";
        }

        const payload = {
          entity_type: "user",
          columns: ["user_id", "full_name"],
          parameter: [
            {
              columnName: "status",
              condition: "=",
              value: "ACTIVE",
            },
            {
              columnName: "role_name",
              condition: "=",
              value: supervisorRole,
            },
            {
              columnName: "org_id",
              condition: "=",
              value: effectiveOrgId,
            },
          ],
        };
        const response = await fetchSupervisors(payload);
        console.log("Supervisors response for role", selectedRoleName, ":", response);

        if (response.businessStatusCode === 2) {
          setSupervisors(response?.list || []);
        }
      } catch (err) {
        console.error("Error fetching supervisors:", err);
      }
    };

    fetchSupervisorForRole();
    // Clear the selected supervisors when role changes (supervisor list will be different)
    setFormData(prev => ({ ...prev, supervisorIds: [] }));
  }, [selectedRoleName, effectiveOrgId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "mobile") {
      const regex = /^\d{0,10}$/;
      if (!regex.test(value)) return;
    }
    
    // If role is being changed, update the selected role name
    if (name === "roleId") {
      setSelectedRoleName(value);
    }
    
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let response;
      // Get primary supervisor (first one in the array) for backward compatibility
      const primarySupervisorId = formData.supervisorIds.length > 0 ? formData.supervisorIds[0] : "";

      if (editData) {
        // Edit mode - update existing member
        const payload = {
          entity_type: "user",
          comment: "Profile updated",
          old_data: {
            full_name: editData.full_name || "",
            email: editData.email || "",
            mobile: editData.mobile || "",
            employee_id: editData.employee_id || "",
            designation: editData.designation || "",
            supervisor_id: editData.supervisor_id || "",
            role_id: editData.role_id || "",
            aadhaar_authentication: editData.aadhaar_authentication || false,
            dob: editData.dob || "",
            user_id: editData.user_id || "",
            org_id: editData.org_id || "",
          },
          new_data: {
            full_name: formData.fullName,
            email: formData.email,
            mobile: formData.mobile,
            employee_id: formData.employeeId || "",
            designation: formData.designation || "",
            supervisor_id: primarySupervisorId, // Primary supervisor for backward compatibility
            supervisor_ids: formData.supervisorIds, // All selected supervisors
            role_name: selectedRoleName, // Send role name (e.g., "MEMBER") instead of role_id (UUID)
            aadhaar_authentication: formData.aadhaarAuthentication,
            dob: formData.dob,
            user_id: editData.user_id,
            org_id: editData.org_id,
          }
        };
        response = await updateMemberAPI(payload);
      } else {
        // Add mode - create new member
        const payload = {
          email: formData.email,
          fullName: formData.fullName,
          mobile: formData.mobile,
          employeeId: formData.employeeId || "",
          designation: formData.designation || "",
          supervisorIds: formData.supervisorIds, // All selected supervisors
          roleId: selectedRoleName, // Send role name (e.g., "MEMBER") instead of role_id (UUID)
          aadhaarAuthentication: formData.aadhaarAuthentication ? true : false,
          dob: formData.dob,
        };
        // If Super Admin has selected an organization, include org_id in payload
        if (isSuperAdmin && superAdminSelectedOrgId) {
          payload.org_id = superAdminSelectedOrgId;
        }
        response = await inviteMember(payload);
      }

      // Handle response based on businessStatusCode
      const businessStatusCode = response?.data?.businessStatusCode;
      const message = response?.data?.message;

      if (businessStatusCode === 1) {
        // Error case - show toast and keep popup open
        toast.error(message || "Failed to add member");
      } else if (businessStatusCode === 2) {
        // Success case - show toast and close popup
        toast.success(message || "Member added successfully");
        onSuccess();
        onClose();
      } else {
        // Unexpected status code
        toast.warning(message || "Unexpected response from server");
      }
    } catch (err) {
      setError(editData ? "Failed to update member" : "Failed to send invite");
      toast.error(err?.response?.data?.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    // Check if role is Manager or Admin (case insensitive)
    const isMandatoryRole = selectedRoleName && 
      (selectedRoleName.toUpperCase() === "MANAGER" || selectedRoleName.toUpperCase() === "ADMIN");
    
    // Check if role is Member (case insensitive)
    const isMemberRole = selectedRoleName && selectedRoleName.toUpperCase() === "MEMBER";
    
    // Base validation - always required fields
    const baseValid = formData.fullName && formData.roleId && formData.dob;
    
    // Mobile number validation - if provided, must be exactly 10 digits
    const isMobileValid = !formData.mobile || formData.mobile.length === 10;
    
    // If Manager or Admin, email and mobile are mandatory (and mobile must be 10 digits)
    if (isMandatoryRole) {
      return baseValid && formData.email && formData.mobile && formData.mobile.length === 10;
    }
    
    // If Member role, at least one supervisor is mandatory
    if (isMemberRole) {
      return baseValid && isMobileValid && formData.supervisorIds.length > 0;
    }
    
    // For other roles, email and mobile are optional but if mobile is provided, it must be 10 digits
    return baseValid && isMobileValid;
  };

  // Normalized, always-a-string role used throughout the render below.
  // selectedRoleName can be undefined/null (e.g. edit data with a null
  // role_name), and the JSX calls .toUpperCase() unguarded in several places;
  // a non-string there throws during render and blanks the whole modal.
  const roleUpper = (selectedRoleName || "").toUpperCase();

  if (!isOpen) return null;

  return (
    <div
      className="add-member-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        padding: "20px",
      }}
    >
      <div
        className="add-member-modal"
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "720px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          animation: "modalSlideIn 0.3s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "24px 28px",
            borderBottom: "1px solid #e5e7eb",
            background: "linear-gradient(to right, #fff 0%, #fff9f5 100%)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #ff7a00 0%, #ffb366 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              {editData ? "âœŽ" : "+"}
            </div>
            <div>
              <h6
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#1f2937",
                  letterSpacing: "-0.01em",
                }}
              >
                {editData ? "Edit User" : "Add New User"}
              </h6>
              <p
                style={{
                  margin: "2px 0 0 0",
                  fontSize: "13px",
                  color: "#6b7280",
                }}
              >
                {editData ? "Update user information" : "Invite a new team member"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#9ca3af",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "6px",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#fee2e2";
              e.currentTarget.style.color = "#dc2626";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#9ca3af";
            }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            overflowY: "auto",
            padding: "28px",
            flex: 1,
          }}
        >
          {!editData && (
            <div
              style={{
                background: "linear-gradient(135deg, #fff9f5 0%, #ffedd5 100%)",
                border: "1px solid #fed7aa",
                borderRadius: "10px",
                padding: "16px 20px",
                marginBottom: "24px",
                display: "flex",
                alignItems: "start",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: "#ff7a00",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "16px",
                  flexShrink: 0,
                }}
              >
                âœ‰
              </div>
              <div>
                <h3
                  style={{
                    margin: "0 0 6px 0",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#92400e",
                  }}
                >
                  Email Invitation
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#b45309",
                    lineHeight: "1.5",
                  }}
                >
                  An email invitation will be sent with account setup instructions.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label
                htmlFor="fullName"
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Full Name (As per Aadhar) <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                name="fullName"
                placeholder="Enter full name"
                value={formData.fullName}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ff7a00";
                  e.target.style.boxShadow = "0 0 0 3px rgba(255, 122, 0, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
                required
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  htmlFor="roleId"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Role <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    outline: "none",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff7a00";
                    e.target.style.boxShadow = "0 0 0 3px rgba(255, 122, 0, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                  required
                >
                  <option value="" disabled>
                    Select a role
                  </option>
                  {roles && roles.length > 0 ? (
                    roles.map((role) => (
                      <option key={role.role_name} value={role.role_name}>
                        {role.role_name}
                      </option>
                    ))
                  ) : (
                    <option value="">No roles available</option>
                  )}
                </select>
              </div>
              <div>
                <label
                  htmlFor="employeeId"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Employee ID
                </label>
                <input
                  type="text"
                  name="employeeId"
                  placeholder="Enter employee ID"
                  value={formData.employeeId}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff7a00";
                    e.target.style.boxShadow = "0 0 0 3px rgba(255, 122, 0, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                htmlFor="designation"
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#374151",
                  marginBottom: "6px",
                }}
              >
                Designation
                <span style={{ color: "#9ca3af", fontWeight: "400" }}> (optional)</span>
              </label>
              <input
                type="text"
                name="designation"
                placeholder="Enter designation (e.g., Software Engineer)"
                value={formData.designation}
                onChange={handleInputChange}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "14px",
                  transition: "all 0.2s",
                  outline: "none",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#ff7a00";
                  e.target.style.boxShadow = "0 0 0 3px rgba(255, 122, 0, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  htmlFor="mobile"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Mobile No.
                  {roleUpper === "MANAGER" ||
                  roleUpper === "ADMIN" ? (
                    <span style={{ color: "#dc2626" }}>*</span>
                  ) : (
                    <span style={{ color: "#9ca3af", fontWeight: "400" }}>
                      {" "}
                      (optional)
                    </span>
                  )}
                </label>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff7a00";
                    e.target.style.boxShadow = "0 0 0 3px rgba(255, 122, 0, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                  pattern="\d{10}"
                  title="Mobile number must be 10 digits"
                  required={
                    roleUpper === "MANAGER" ||
                    roleUpper === "ADMIN"
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Email
                  {roleUpper === "MANAGER" ||
                  roleUpper === "ADMIN" ? (
                    <span style={{ color: "#dc2626" }}>*</span>
                  ) : (
                    <span style={{ color: "#9ca3af", fontWeight: "400" }}>
                      {" "}
                      (optional)
                    </span>
                  )}
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff7a00";
                    e.target.style.boxShadow = "0 0 0 3px rgba(255, 122, 0, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                  required={
                    roleUpper === "MANAGER" ||
                    roleUpper === "ADMIN"
                  }
                />
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              <div>
                <label
                  htmlFor="dob"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Date of Birth (As per Aadhar)<span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="date"
                  name="dob"
                  id="dob"
                  value={formData.dob}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    transition: "all 0.2s",
                    outline: "none",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#ff7a00";
                    e.target.style.boxShadow = "0 0 0 3px rgba(255, 122, 0, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#e5e7eb";
                    e.target.style.boxShadow = "none";
                  }}
                  required
                />
              </div>
              <div>
                <label
                  htmlFor="supervisorIds"
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "6px",
                  }}
                >
                  Supervisor(s)
                  {roleUpper === "MEMBER" ? (
                    <span style={{ color: "#dc2626" }}>*</span>
                  ) : (
                    <span style={{ color: "#9ca3af", fontWeight: "400" }}>
                      {" "}
                      (optional)
                    </span>
                  )}
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={Array.isArray(formData.supervisorIds) ? formData.supervisorIds : []}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        supervisorIds: Array.isArray(val) ? val : [val],
                      }));
                    }}
                    input={<OutlinedInput />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(Array.isArray(selected) ? selected : []).map((value) => {
                          const supervisor = supervisors.find(s => s.user_id === value);
                          return (
                            <Chip
                              key={value}
                              label={supervisor?.full_name || value}
                              size="small"
                              sx={{
                                backgroundColor: '#ff7a00',
                                color: 'white',
                                '& .MuiChip-deleteIcon': { color: 'white' }
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e5e7eb',
                        borderWidth: '1.5px',
                        borderRadius: '8px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ff7a00',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ff7a00',
                        boxShadow: '0 0 0 3px rgba(255, 122, 0, 0.1)',
                      },
                    }}
                  >
                    {supervisors && supervisors.length > 0 ? (
                      supervisors.map((supervisor) => (
                        <MenuItem key={supervisor.user_id} value={supervisor.user_id}>
                          <Checkbox
                            checked={formData.supervisorIds.includes(supervisor.user_id)}
                            sx={{
                              '&.Mui-checked': {
                                color: '#ff7a00',
                              },
                            }}
                          />
                          <ListItemText primary={supervisor.full_name || "Unnamed Supervisor"} />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No supervisor available</MenuItem>
                    )}
                  </Select>
                </FormControl>
                {formData.supervisorIds.length > 0 && (
                  <div style={{ marginTop: "6px", fontSize: "12px", color: "#6b7280" }}>
                    Selected: {formData.supervisorIds.length} supervisor(s)
                  </div>
                )}
              </div>
            </div>

            {country === "India" && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "14px 16px",
                  background: "#f9fafb",
                  border: "1.5px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                  }}
                  htmlFor="aadhaarAuthentication"
                >
                  <input
                    type="checkbox"
                    name="aadhaarAuthentication"
                    id="aadhaarAuthentication"
                    checked={formData.aadhaarAuthentication}
                    onChange={handleInputChange}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      accentColor: "#ff7a00",
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#374151",
                        display: "block",
                        marginBottom: "2px",
                      }}
                    >
                      Aadhaar Authentication
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      Enable Aadhaar-based identity verification (Recommended)
                    </span>
                  </div>
                </label>
              </div>
            )}

            {error && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "12px 16px",
                  background: "#fee2e2",
                  border: "1px solid #fecaca",
                  borderRadius: "8px",
                  color: "#991b1b",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "16px" }}>âš </span>
                {error}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                paddingTop: "8px",
              }}
            >
              <ArthaCancelButton
                type="button"
                onClick={onClose}
                style={{
                  padding: "10px 24px",
                  fontSize: "14px",
                  fontWeight: "600",
                }}
              >
                Cancel
              </ArthaCancelButton>
              <ArthaContinueButton
                type="submit"
                style={{
                  padding: "10px 28px",
                  fontSize: "14px",
                  fontWeight: "600",
                  opacity: isSuspended ? 0.5 : 1,
                  cursor: isSuspended ? "not-allowed" : "pointer",
                }}
                disabled={isLoading || !isFormValid() || isSuspended}
                title={isSuspended ? "Actions are disabled - organization is suspended" : ""}
              >
                {isLoading
                  ? editData
                    ? "Updating..."
                    : "Saving..."
                  : editData
                  ? "Update User"
                  : "Add"}
              </ArthaContinueButton>
            </div>
          </form>
        </div>
      </div>

      <style>
        {`
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          .add-member-modal::-webkit-scrollbar {
            width: 8px;
          }

          .add-member-modal::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
          }

          .add-member-modal::-webkit-scrollbar-thumb {
            background: #d1d5db;
            border-radius: 10px;
          }

          .add-member-modal::-webkit-scrollbar-thumb:hover {
            background: #9ca3af;
          }

          /* Mobile responsive styles */
          @media (max-width: 768px) {
            .add-member-overlay {
              padding: 10px !important;
              align-items: flex-start !important;
              padding-top: 20px !important;
            }
            .add-member-modal {
              max-height: 95vh !important;
              max-width: 100% !important;
              margin: 0 !important;
              border-radius: 8px !important;
            }
            .add-member-modal > div:first-child {
              padding: 16px 16px !important;
            }
            .add-member-modal > div:last-child {
              padding: 16px !important;
            }
          }

          /* Form grid responsive */
          @media (max-width: 576px) {
            .add-member-modal form > div[style*="grid-template-columns"] {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
            }
            .add-member-modal input,
            .add-member-modal select {
              font-size: 16px !important; /* Prevents zoom on iOS */
              padding: 12px !important;
            }
            .add-member-modal label {
              font-size: 12px !important;
            }
            .add-member-modal h6 {
              font-size: 16px !important;
            }
            .add-member-modal form > div:last-child {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .add-member-modal form > div:last-child button {
              width: 100% !important;
              margin: 0 !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default AddMember;
